import { travelDay } from './calendar'
import { dayLabel } from './format'
import { usablePlaceCoord } from './geocode'
import { haversineM } from './gpx'
import { MAX_GEOMETRY_POINTS, routeGeometry, thinGeometry } from './route'
import { tulipKm, tulipShapeInfo, tulipText } from './tulip'
import { fahrzeug } from './vehicle'
import type { LatLon, PhotoMeta, Stage, Trip, Tulip } from './types'
import { zipSchreiben, type ZipEintrag } from './zip'

/**
 * Die Reise für ein Navigationsgerät ausgeben: GPX und KML/KMZ.
 *
 * Reine Funktionen, kein DOM, kein React, kein Netz. Die Bedienung steht in
 * features/data/NavExport.tsx, hier stehen nur die Regeln - und die sind ohne
 * Browser prüfbar. Gebaut wird alles aus dem lokalen Bestand, damit der Export
 * auch im Funkloch geht.
 *
 * Warum zwei Formate: sie können Verschiedenes.
 *
 * GPX ist das Navigationsformat. Es unterscheidet als einziges nativ zwischen
 * "da fahren wir hin" und "das zeigen wir nur an": rtept innerhalb einer rte
 * sind Navigationsziele, freistehende wpt werden angezeigt und von OsmAnd
 * unterwegs vorgelesen, aber nicht angefahren. Die geplante Linie muss zudem
 * als Garmin-Formpunkte im jeweils beginnenden rtept stehen. Ein Track allein
 * ist nur eine Zeichnung: Bei der Testfahrt Basel - Münchenbuchsee wich das
 * Gerät auf 86 von 90 km im Median nur 2 m ab, berechnete ohne Formpunkte aber
 * einen 4,17 km langen eigenen Abschnitt bis 872 m neben der Planung und mit
 * 2,44 km Umweg. Bilder kann GPX nicht.
 *
 * KML ist das Anzeigeformat für Google My Maps und Google Earth. Es kann HTML
 * in der Beschreibung - also Tabellen und Fotos -, Ordner zum Gruppieren und
 * eigene Symbole. Navigieren kann Google Maps damit nicht; es zeigt die Karte,
 * und wer einen Punkt antippt, kann von dort aus navigieren.
 *
 * Wie die Ordner der KML geschnitten werden, ist wählbar - siehe
 * NavGliederung. Das ist keine Geschmacksfrage: Google My Maps nimmt zehn
 * Ebenen je Karte, und eine Reise hat mehr Etappen als das.
 *
 * Die Erweiterungen der GPX-Datei sind so gebaut, dass jedes Gerät überliest,
 * was es nicht kennt (GPX 1.1, extensionsType: xsd:any mit processContents
 * lax). Deshalb stehen OsmAnd und Garmin nebeneinander in derselben Datei,
 * dazu ein neutrales sym für alles andere.
 *
 * Belege für die Erweiterungen:
 * - OsmAnd: https://osmand.net/docs/technical/osmand-file-formats/osmand-gpx
 *   (osmand:color, osmand:icon, osmand:background mit circle, octagon, square;
 *   osmand:points_groups mit einer group je Gruppe; die Gruppe eines Punktes
 *   steht in dessen type).
 * - Garmin: GpxExtensionsv3.xsd, WaypointExtension_t mit der Reihenfolge
 *   Proximity, Temperature, Depth, DisplayMode, Categories, Address,
 *   PhoneNumber. Proximity ist ein Warnradius in Metern je Punkt.
 * - KML 2.2: http://schemas.opengis.net/kml/2.2.0/ogckml22.xsd.
 *
 * Zwei Fallen, gegen die es hier Tests gibt: GPX schreibt Breite und Länge als
 * Attribute (lat, lon), KML schreibt Länge und Breite als Text und in dieser
 * Reihenfolge (lon,lat) - und die Farbe in KML ist aabbggrr, nicht rrggbb.
 */

// --- Was in die Datei kommt ---------------------------------------------------

/**
 * Wofür ein Punkt da ist.
 *
 * Nicht die Herkunft aus dem Datenmodell, sondern die Aussage auf der Karte:
 * daran hängen Symbol, Farbe, Gruppe und Warnradius. Ein Grenzübergang aus
 * einer Tulpe und einer aus einer künftigen anderen Quelle sähen gleich aus,
 * und das ist richtig so.
 */
export type NavArt = 'ziel' | 'zwischen' | 'camping' | 'grenze' | 'faehre' | 'tulpe' | 'hinweis'

/**
 * Wie die Ordner der KML geschnitten werden.
 *
 * Nur die KML kennt Ordner. Die GPX hat keine - sie gruppiert über den type
 * eines Wegpunktes -, und deshalb ändert diese Wahl an der GPX nichts.
 *
 * 'etappe' legt einen Ordner je Tag an. Das ist die Gliederung, in der man
 * denkt: einen Tag ein- und ausblenden, den Rest stehen lassen. Google Earth
 * und OsmAnd machen das mit, sie kennen keine Obergrenze für Ebenen.
 *
 * 'art' legt einen Ordner je Art an: Ziele, Camping, Grenzen, Tulpen,
 * Hinweise, Strecken. Sechs Ordner, gleich viele Objekte - nur anders
 * einsortiert.
 *
 * Der Unterschied ist einer der Grenzen und nicht des Geschmacks. Google My
 * Maps nimmt zehn Ebenen je Karte (MYMAPS_EBENEN) und 2000 Objekte je Ebene
 * (MYMAPS_OBJEKTE_JE_EBENE). Nach Etappe gegliedert stehen die Objekte dünn in
 * vielen Ebenen, nach Art dicht in wenigen - die eine Gliederung läuft in die
 * Ebenengrenze, die andere in die Objektgrenze.
 */
export type NavGliederung = 'etappe' | 'art'

/** Die Gliederungen in der Reihenfolge, in der sie zur Wahl stehen. */
export const NAV_GLIEDERUNGEN: NavGliederung[] = ['art', 'etappe']

/**
 * Die Vorgabe: nach Art.
 *
 * Nicht, weil sie die schönere wäre - nach Etappe zu gliedern entspricht dem,
 * was man vorhat. Sondern weil eine Reise mehr als zehn Etappen hat und Google
 * My Maps zehn Ebenen nimmt. Eine Vorgabe, die beim ersten Öffnen der App eine
 * Warnung auslöst und im Ziel, das ohne Einarbeitung erreichbar ist, die
 * hinteren Tage stillschweigend fallen lässt, ist keine.
 *
 * Google Earth und OsmAnd kommen mit beiden zurecht; wer dort einzelne Tage
 * schalten will, stellt eine Wahl weiter um.
 */
export const NAV_GLIEDERUNG_VORGABE: NavGliederung = 'art'

/** Name einer Gliederung, wie er in der Oberfläche und in Meldungen steht. */
export function navGliederungName(gliederung: NavGliederung): string {
  return gliederung === 'art' ? 'nach Art' : 'nach Etappe'
}

/** Eine Zeile der Beschreibung: Bezeichnung und Wert. */
export interface NavZeile {
  /** Leer heisst: ein Satz ohne Etikett, etwa ein Hinweis. */
  name: string
  wert: string
}

/** Ein Foto, wie es in der KMZ liegt. */
export interface NavFoto {
  id: string
  /** Pfad im Archiv, etwa "fotos/p1.jpg". */
  pfad: string
  /** Grösse in Bytes laut Bestand - für die Vorschau, ohne die Bilder zu laden. */
  bytes: number
}

/** Ein Punkt der Ausgabe. */
export interface NavPunkt {
  art: NavArt
  /** Wo er liegt, als [Breite, Länge] - wie überall im Datenmodell. */
  at: LatLon
  name: string
  zeilen: NavZeile[]
  /** Nur am Etappenziel und nur für die KMZ; GPX trägt keine Bilder. */
  fotos: NavFoto[]
  /**
   * Ob der Name schon mit "Tag NN" beginnt.
   *
   * Wird gebraucht, sobald die KML nach Art gegliedert ist: dann sagt kein
   * Ordner mehr, zu welchem Tag ein Punkt gehört, und die Tagesnummer muss in
   * den Namen. Beim Etappenziel und beim Hinweis steht sie dort schon, beim
   * Campingplatz und bei der Tulpe nicht - und "Tag 03 · Tag 03 · Bovec" wäre
   * kein Fortschritt. Ein Merkmal am Punkt und keine Prüfung auf die
   * Zeichenfolge: wer den Namen ändert, ändert ihn an einer Stelle.
   */
  tagImNamen: boolean
}

/**
 * Eine Etappe der Ausgabe: ein Abschnitt in der GPX, und in der KML entweder
 * ein eigener Ordner oder auf die Ordner der Arten verteilt - siehe
 * NavGliederung.
 */
export interface NavEtappe {
  id: string
  /** Der Reisetag dieser Etappe, ab eins. Siehe lib/calendar.ts. */
  tag: number
  /** Überschrift des Ordners, etwa "Tag 03 · Bled → Bovec". */
  name: string
  /** Was an diesem Tag gefahren wird, für die Beschreibung des Ordners. */
  zeilen: NavZeile[]
  start: LatLon
  /**
   * Die Zwischenpunkte dieses Tages, in Fahrreihenfolge: in GPX je ein rtept
   * vor dem Etappenziel, in KML eine Ortsmarke.
   *
   * Hier liegt der praktische Gewinn der ganzen Sache. Ein Navigationsgerät,
   * das nur Start und Ziel bekommt, rechnet sich seinen eigenen Weg dorthin -
   * und das ist für Bled - Bovec der Karawankentunnel und nicht der Vršič.
   * Steht der Pass als rtept zwischen beiden, fährt das Gerät ihn.
   */
  zwischen: NavPunkt[]
  /** Das Etappenziel: in GPX ein rtept, in KML eine Ortsmarke. */
  ziel: NavPunkt
  /** Alles, was nur angezeigt wird: Camping, Grenzen, Tulpen, Hinweise. */
  punkte: NavPunkt[]
  /**
   * Die berechnete Strecke, oder leer wenn es keine aktuelle gibt.
   *
   * Höchstens MAX_GEOMETRY_POINTS Stützpunkte - siehe sammleNavExport.
   */
  linie: LatLon[]
}

/** Die ganze Ausgabe, fertig sortiert und ohne alles Weggelassene. */
export interface NavExport {
  titel: string
  beschreibung: string
  etappen: NavEtappe[]
  /**
   * Tulpen ohne Kilometerstand.
   *
   * Sie stehen in keiner Datei, und das ist kein Mangel der Berechnung,
   * sondern eine fehlende Angabe: ohne Kilometerstand gibt es keine Stelle auf
   * der Strecke, an die der Punkt gehörte. Der Zustand ist normal - die Zeile
   * wird am Strassenrand notiert, bevor jemand den Tacho abgelesen hat.
   *
   * Getrennt von ohnePosition gezählt, weil beides an eine andere Stelle
   * führt: hier hilft nur der Tacho, dort der Knopf im Reiter Etappen.
   */
  ohneKm: number
  /**
   * Tulpen mit Kilometerstand, für die keine Position gerechnet werden konnte.
   *
   * Sie stehen in keiner Datei. Eine geratene Position wäre schlimmer als
   * keine: ein Wegpunkt auf der Karte ist eine Behauptung über einen Ort.
   */
  ohnePosition: number
  /** Etappen ohne aktuelle berechnete Strecke: ohne Linie und ohne Tulpen. */
  ohneStrecke: number
  /** Ausgelassene Etappen, die gar nicht erst in die Datei kommen. */
  ausgelassen: number
}

/**
 * Aussehen und Verhalten je Art, an einer Stelle.
 *
 * Die Symbolnamen von OsmAnd folgen dem Muster tag_wert aus dessen
 * POI-Katalog (poi_types.xml in OsmAnd-resources): tourism=camp_site wird zu
 * tourism_camp_site. Was OsmAnd nicht kennt, fällt dort auf ein
 * Ersatzsymbol zurück - ein falscher Name kostet also das Symbol und nicht
 * den Punkt.
 *
 * Die Symbole der KML sind die Standardpalette von Google und werden von
 * Google Earth wie von My Maps verstanden.
 */
interface ArtInfo {
  /** Name der Gruppe in GPX (type) und der Kategorie bei Garmin. */
  gruppe: string
  /**
   * Name des Ordners in einer nach Art gegliederten KML.
   *
   * Mehrzahl, denn dort liegen alle Punkte dieser Art beieinander - und ein
   * eigener Name, weil gruppe von OsmAnd und Garmin gelesen wird. Wer den
   * Ordner "Tulpen" nennen will, soll dafür nicht die Gruppe umbenennen
   * müssen, die auf dem Navigationsgerät steht.
   */
  ordner: string
  /** Was in diesem Ordner liegt, als ein Satz für dessen Beschreibung. */
  ordnerText: string
  /** Farbe als #rrggbb, aus der Palette der App (styles/tokens.css). */
  farbe: string
  /** Symbolname bei OsmAnd. */
  osmandIcon: string
  /** Hintergrundform bei OsmAnd: circle, octagon oder square. */
  osmandForm: 'circle' | 'octagon' | 'square'
  /** Neutrales Symbol für Geräte ohne Erweiterungen. */
  sym: string
  /**
   * Warnradius in Metern (Garmin gpxx:Proximity).
   *
   * Grenze und Fähre bekommen den grössten, zwei Kilometer. An der Grenze geht
   * es um Papiere, Bargeld und die Frage, ob man noch tanken sollte; an der
   * Fähre um eine Abfahrtszeit, um die Schlange davor und darum, ob man das
   * Ticket schon hat. Beides sind Fragen, die dreihundert Meter vorher nichts
   * mehr nützen - und bei der Fähre ist der Schaden am grössten, denn die
   * nächste geht in zwei Stunden.
   *
   * Eine Tulpe dagegen meint eine einzelne Kreuzung; ein grosser Radius machte
   * daraus eine Warnung, die dreihundert Meter vor der falschen Abzweigung
   * losgeht.
   */
  radiusM: number
  /** Symbol in der KML, aus Googles Standardpalette. */
  kmlIcon: string
}

const KML_SYMBOLE = 'https://maps.google.com/mapfiles/kml/shapes/'

const ARTEN: Record<NavArt, ArtInfo> = {
  ziel: {
    gruppe: 'Etappenziel',
    ordner: 'Ziele',
    ordnerText: 'Wo der Tag endet - ein Punkt je Etappe, mit den Fotos.',
    farbe: '#1e2a33',
    osmandIcon: 'special_flag_stroke',
    osmandForm: 'circle',
    sym: 'Flag, Blue',
    radiusM: 0,
    kmlIcon: `${KML_SYMBOLE}flag.png`,
  },
  zwischen: {
    gruppe: 'Zwischenpunkt',
    ordner: 'Zwischenpunkte',
    ordnerText: 'Die Stellen, über die die Strecke geführt wird - Pässe, Schluchten, Umwege.',
    farbe: '#2f5d3a',
    osmandIcon: 'special_symbol_triangle',
    osmandForm: 'circle',
    sym: 'Waypoint',
    radiusM: 0,
    kmlIcon: `${KML_SYMBOLE}triangle.png`,
  },
  camping: {
    gruppe: 'Camping',
    ordner: 'Camping',
    ordnerText: 'Die Übernachtungen, mit Preis und Notiz.',
    farbe: '#2e7d8a',
    osmandIcon: 'tourism_camp_site',
    osmandForm: 'circle',
    sym: 'Campground',
    radiusM: 500,
    kmlIcon: `${KML_SYMBOLE}campground.png`,
  },
  grenze: {
    gruppe: 'Grenze',
    ordner: 'Grenzen',
    ordnerText: 'Die Übergänge aus den Grenz-Tulpen.',
    farbe: '#a8322d',
    osmandIcon: 'barrier_border_control',
    osmandForm: 'octagon',
    sym: 'Danger Area',
    radiusM: 2000,
    kmlIcon: `${KML_SYMBOLE}caution.png`,
  },
  faehre: {
    gruppe: 'Fähre',
    ordner: 'Fähren',
    ordnerText: 'Die Überfahrten aus den Fähr-Tulpen - mit der Abfahrtszeit aus dem Hinweis.',
    farbe: '#2e7d8a',
    osmandIcon: 'transport_ferry_terminal',
    osmandForm: 'octagon',
    sym: 'Anchor',
    radiusM: 2000,
    kmlIcon: `${KML_SYMBOLE}ferry.png`,
  },
  tulpe: {
    gruppe: 'Roadbook',
    ordner: 'Tulpen',
    ordnerText: 'Die Zeilen des Roadbooks, gesetzt nach ihrem Kilometerstand.',
    farbe: '#8a6a12',
    osmandIcon: 'special_marker',
    osmandForm: 'square',
    sym: 'Waypoint',
    radiusM: 300,
    kmlIcon: `${KML_SYMBOLE}arrow.png`,
  },
  hinweis: {
    gruppe: 'Unterwegs',
    ordner: 'Hinweise',
    ordnerText: 'Was für die Fahrt gilt - eine Marke am Start jedes Tages.',
    farbe: '#d9a626',
    osmandIcon: 'special_information',
    osmandForm: 'circle',
    sym: 'Information',
    radiusM: 1000,
    kmlIcon: `${KML_SYMBOLE}info-i.png`,
  },
}

/** Die Arten in der Reihenfolge, in der sie in Listen und Gruppen stehen. */
export const NAV_ARTEN: NavArt[] = [
  'ziel',
  'zwischen',
  'camping',
  'grenze',
  'faehre',
  'tulpe',
  'hinweis',
]

/**
 * Die Arten, die in der GPX zu einem Routenpunkt werden statt zu einem
 * Wegpunkt.
 *
 * Ein rtept ist ein Ziel, das angefahren wird; ein wpt wird angezeigt und
 * vorgelesen, aber nicht angefahren. Etappenziel und Zwischenpunkt sind beides
 * Ersteres - und deshalb stehen sie nicht zusätzlich als Wegpunkt in der
 * Datei: zwei Nadeln auf demselben Ort sind zwei Nadeln, die einander
 * verdecken, und die eine davon wäre eine Behauptung, dass dort noch etwas
 * anderes liegt.
 */
const ALS_ROUTENPUNKT: NavArt[] = ['ziel', 'zwischen']

/** Name einer Art, wie er in der Oberfläche und in den Dateien steht. */
export function navArtName(art: NavArt): string {
  return ARTEN[art].gruppe
}

/**
 * Der Ordner der Strecken in einer nach Art gegliederten KML.
 *
 * Er steht neben den fünf Arten und nicht in ihnen: eine Strecke ist keine
 * Ortsmarke, sondern eine Linie. Für Google zählt sie trotzdem als Objekt.
 */
const ORDNER_STRECKEN = 'Strecken'
const ORDNER_STRECKEN_TEXT = 'Die berechneten Strassen, eine Linie je Etappe.'

/** Die Namen der Ordner einer nach Art gegliederten KML, in Dateireihenfolge. */
export const NAV_ORDNER_NACH_ART: string[] = [
  ...NAV_ARTEN.map((art) => ARTEN[art].ordner),
  ORDNER_STRECKEN,
]

// --- Die Position einer Tulpe -------------------------------------------------

/**
 * Wie weit der Kilometerstand einer Tulpe über die berechnete Strecke
 * hinausgehen darf, bevor der Punkt wegfällt.
 *
 * Ein Kilometer. Die beiden Zahlen kommen aus verschiedenen Quellen: der
 * Kilometerstand ist am Strassenrand vom Tacho abgelesen und auf hundert Meter
 * notiert, die Streckenlänge ist die Rechnung eines Routendienstes. Dass sie
 * am Ende der Etappe um ein paar hundert Meter auseinanderliegen, ist der
 * Normalfall und kein Grund, die letzte Zeile des Roadbooks wegzuwerfen. Wer
 * dagegen zwanzig Kilometer über die Strecke hinaus notiert hat, ist eine
 * andere Strasse gefahren - und wo diese Stelle liegt, weiss dann niemand.
 */
export const TULPE_TOLERANZ_KM = 1

/**
 * Die Stelle auf einer Linie, die einem Kilometerstand entspricht.
 *
 * Gerechnet wird über den Anteil an der Gesamtstrecke und nicht über die
 * gemessene Länge der Linie selbst, und das ist der Kern der Sache: die
 * gespeicherte Geometrie ist ausgedünnt (siehe thinGeometry in lib/route.ts),
 * schneidet also Kurven ab und ist damit kürzer als die Strasse. Bei 12
 * Kilometern absolut abgetragen läge der Punkt deshalb systematisch zu weit
 * vorn, und zwar umso mehr, je kurviger die Etappe war - am Vršič mit seinen
 * fünfzig Kehren am meisten. Über den Anteil gerechnet stimmt die Stelle
 * dagegen, solange die Ausdünnung gleichmässig ist, und sie wird von selbst
 * genauer, sobald die Geometrie es wird. Nichts hier hängt an der heutigen
 * Ausdünnung.
 *
 * @param linie Stützpunkte der Strasse, Start zuerst.
 * @param streckeKm Länge der Strecke laut Routendienst.
 * @param km Kilometerstand ab Beginn der Etappe.
 * @returns null, wenn es dafür keine Stelle gibt: ohne Linie, ohne Strecke,
 *   ohne Kilometerstand (NaN heisst "noch nicht bekannt") oder wenn der Stand
 *   weiter reicht als die Strecke.
 */
export function positionBeiKm(linie: LatLon[], streckeKm: number, km: number): LatLon | null {
  if (linie.length < 2) return null
  if (!Number.isFinite(streckeKm) || streckeKm <= 0) return null
  if (!Number.isFinite(km) || km < 0) return null
  if (km > streckeKm + TULPE_TOLERANZ_KM) return null

  const anteil = Math.min(1, km / streckeKm)

  // Die Abstände zwischen den Stützpunkten einmal durchgehen und dabei die
  // Gesamtlänge bilden. Zwei Durchläufe sind es, weil das Ziel erst feststeht,
  // wenn die Länge bekannt ist - bei ein paar hundert Punkten je Etappe ist
  // das eine Nebensache.
  const teile: number[] = []
  let gesamt = 0
  for (let i = 1; i < linie.length; i++) {
    const d = haversineM(linie[i - 1]!, linie[i]!)
    teile.push(d)
    gesamt += d
  }
  if (gesamt <= 0) return null

  const ziel = anteil * gesamt
  let gelaufen = 0
  for (let i = 0; i < teile.length; i++) {
    const d = teile[i]!
    if (gelaufen + d >= ziel) {
      const a = linie[i]!
      const b = linie[i + 1]!
      // Zwischen zwei Stützpunkten wird linear geteilt. Über die wenigen
      // Kilometer eines Abschnitts ist der Unterschied zur Kugel wenige
      // Zentimeter, und die Stelle selbst ist ohnehin eine Schätzung aus
      // einem abgelesenen Tachostand.
      const t = d === 0 ? 0 : (ziel - gelaufen) / d
      return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t]
    }
    gelaufen += d
  }

  return linie[linie.length - 1]!
}

// --- Aus dem Bestand die Ausgabe bauen ----------------------------------------

/** Was der Export aus dem Bestand mitnehmen soll. */
export interface NavAuswahl {
  /** Nur diese Etappe, oder leer für die ganze Reise. */
  stageId?: string
  /** Die Fotos je Etappe, für die KMZ. Fehlt, wenn keine mitsollen. */
  fotos?: Record<string, PhotoMeta[]>
}

function zeile(name: string, wert: string): NavZeile[] {
  const sauber = wert.trim()
  return sauber === '' ? [] : [{ name, wert: sauber }]
}

/**
 * Die Koordinate eines Übernachtungsplatzes, sofern es eine brauchbare ist.
 *
 * Geprüft wird auch hier, obwohl beim Eintragen schon geprüft wurde: der
 * Bestand kann durch eine Importdatei oder den Abgleich hereinkommen, und ein
 * Wegpunkt ist eine Behauptung über einen Ort. Ein Platz auf 0/0 schickte
 * jemanden nachts in den Golf von Guinea; ohne Koordinate steht er wenigstens
 * am Etappenziel, und die Beschreibung sagt, dass das nicht seine Adresse ist.
 */
function brauchbareKoordinate(at: LatLon | undefined): LatLon | null {
  if (!at) return null
  const [lat, lon] = at
  return usablePlaceCoord(lat, lon) ? [lat, lon] : null
}

/**
 * Dateiendung eines Bildes aus seinem Medientyp.
 *
 * Aus dem Typ und nicht fest "jpg": die App speichert zwar JPEG (siehe
 * lib/image.ts), aber eine Datei, deren Name etwas anderes behauptet als ihr
 * Inhalt, ist genau die Art Fehler, die erst beim Öffnen auffällt.
 */
function bildEndung(mime: string): string {
  const untertyp =
    mime
      .split('/')[1]
      ?.split(';')[0]
      ?.toLowerCase()
      .replace(/[^a-z0-9]/g, '') ?? ''
  if (untertyp === 'jpeg') return 'jpg'
  return untertyp === '' ? 'bin' : untertyp
}

/**
 * Die Fotos einer Etappe, wie sie in der KMZ liegen.
 *
 * Der Name im Archiv ist die ID des Fotos und nicht sein Platz in der Reihe:
 * IDs sind eindeutig, Zählungen fangen in jedem Ordner wieder bei eins an -
 * und ein Archiv mit zwei Dateien namens fotos/1.jpg wäre kaputt.
 */
function fotosVon(metas: PhotoMeta[] | undefined): NavFoto[] {
  return (metas ?? []).map((meta) => ({
    id: meta.id,
    pfad: `fotos/${meta.id}.${bildEndung(meta.mime)}`,
    bytes: meta.size,
  }))
}

/**
 * Die Zwischenpunkte einer Etappe als Punkte der Ausgabe.
 *
 * Sie kommen mit ihrer eigenen Koordinate und brauchen anders als eine Tulpe
 * keine berechnete Strecke: sie sind der Grund, aus dem die Strecke so verläuft,
 * wie sie verläuft, und nicht eine Stelle darauf. Eine Etappe ohne berechnete
 * Strecke exportiert ihre Zwischenpunkte deshalb trotzdem - das Gerät fährt
 * dann zwar keine Linie ab, hält sich aber an die Reihenfolge der Routenpunkte.
 *
 * Eine unbrauchbare Koordinate lässt den Punkt weg. Sie kann über eine
 * Importdatei oder den Abgleich hereinkommen, und ein Routenpunkt im Golf von
 * Guinea schickte das Navigationsgerät quer über die Weltkarte - das ist
 * schlimmer als ein Punkt weniger, denn hier wird gefahren und nicht nur
 * angezeigt.
 */
function zwischenpunkteVon(stage: Stage): NavPunkt[] {
  const punkte: NavPunkt[] = []
  for (const [i, punkt] of (stage.via ?? []).entries()) {
    const at = brauchbareKoordinate(punkt.at)
    if (!at) continue
    const name = punkt.name.trim()
    punkte.push({
      art: 'zwischen',
      at,
      // Ohne Namen die Nummer: „Zwischenpunkt 2“ sagt wenigstens, das
      // Wievielte des Tages es ist. Ein leerer Name wäre auf dem Gerät eine
      // Nadel, die man nicht ansprechen kann.
      name: name === '' ? `Zwischenpunkt ${i + 1}` : name,
      zeilen: [
        { name: 'Über', wert: `${stage.from} → ${stage.to}` },
        { name: 'Reihenfolge', wert: `${i + 1}. von ${(stage.via ?? []).length}` },
      ],
      fotos: [],
      tagImNamen: false,
    })
  }
  return punkte
}

/**
 * Die Punkte einer Etappe, die nur angezeigt werden.
 *
 * @param tag Der Reisetag der Etappe, zweistellig. Kommt von aussen, weil eine
 *   einzelne Etappe ihn nicht kennt - er hängt daran, wie viele Etappen vor ihr
 *   ausgelassen sind.
 */
function punkteVon(
  stage: Stage,
  tag: string,
  linie: LatLon[],
): { punkte: NavPunkt[]; ohneKm: number; ohnePosition: number } {
  const punkte: NavPunkt[] = []

  // Camping. Hat der Platz eine eigene Koordinate, steht die Marke dort - dann
  // führt das Gerät abends zum Platz und nicht ins Ortszentrum. Hat er keine,
  // bleibt es beim Etappenziel, und die Beschreibung sagt das ausdrücklich:
  // eine Ortsmarke, die eine Adresse behauptet, die niemand eingetragen hat,
  // wäre eine Auskunft zu viel. Die Koordinate ist optional und bleibt es -
  // „Camps um Ilidža“ ist eine Gegend und kein Punkt.
  for (const platz of stage.camping) {
    const name = platz.name.trim()
    if (name === '') continue
    const eigenerOrt = brauchbareKoordinate(platz.at)
    punkte.push({
      art: 'camping',
      at: eigenerOrt ?? stage.c2,
      name,
      zeilen: [
        ...zeile('Preis', platz.price),
        ...zeile('Notiz', platz.note),
        eigenerOrt
          ? { name: 'Ort', wert: 'Eingetragene Lage des Platzes' }
          : { name: 'Ort', wert: `Etappenziel ${stage.to}, nicht die Adresse des Platzes` },
      ],
      fotos: [],
      tagImNamen: false,
    })
  }

  // Die Hinweise des Tages: eine Ortsmarke am Start der Etappe und nicht eine
  // je Hinweis. Sie stehen dort, wo der Tag anfängt, weil sie für die Fahrt
  // gelten und nicht für die Ankunft - "Karawankentunnel 8.80 € extra" nützt
  // beim Losfahren und nicht abends. Und alle in einer Marke, weil drei Nadeln
  // auf demselben Punkt drei Nadeln sind, die einander verdecken.
  const hinweise = [
    ...stage.highlights.map((text) => ({ name: '', wert: text.trim() })),
    ...zeile('Hinweis', stage.tip),
  ].filter((z) => z.wert !== '')
  if (hinweise.length > 0) {
    punkte.push({
      art: 'hinweis',
      at: stage.c1,
      name: `Tag ${tag} · Unterwegs`,
      zeilen: hinweise,
      fotos: [],
      tagImNamen: true,
    })
  }

  // Die Tulpen. Sie tragen einen Kilometerstand und keine Koordinate; ihre
  // Stelle wird aus der berechneten Strecke gerechnet. Ohne Strecke gibt es
  // keine Stelle - dann fällt der Punkt weg und wird gezählt.
  //
  // Gezählt wird nach Ursache und nicht in einem Topf, denn die beiden Ursachen
  // führen an verschiedene Stellen der App. Fehlt der Strecke die Berechnung,
  // hilft der Knopf im Reiter Etappen; fehlt der Tulpe der Kilometerstand -
  // NaN, und das ist ein völlig normaler Zustand, siehe Tulip in types.ts -,
  // dann hilft nur der Tacho. Ein Satz, der beides zusammenwirft, sagt der
  // einen Hälfte der Fälle etwas Falsches: eine Tulpe ohne Kilometerstand hat
  // keinen, und ihre Etappe kann eine tadellos berechnete Strecke haben.
  let ohneKm = 0
  let ohnePosition = 0
  const streckeKm = stage.route?.distanceKm ?? NaN
  for (const tulip of stage.tulips ?? []) {
    // Genau NaN und nichts sonst: das ist der Wert, mit dem eine Tulpe ohne
    // abgelesenen Tacho im Bestand steht (siehe Tulip in types.ts und das
    // leere Feld in TulipEditor). Ein negativer oder unsinniger Kilometerstand
    // ist etwas anderes - da steht eine Angabe, sie ergibt nur keine Stelle.
    if (!Number.isFinite(tulip.km)) {
      ohneKm++
      continue
    }
    const at = positionBeiKm(linie, streckeKm, tulip.km)
    if (!at) {
      ohnePosition++
      continue
    }
    punkte.push(tulpenPunkt(tulip, at, stage))
  }

  return { punkte, ohneKm, ohnePosition }
}

/**
 * Eine Tulpe als Ortsmarke.
 *
 * Der Name ist das, was OsmAnd unterwegs vorliest, wenn die Sprachführung an
 * ist: erst wo, dann was. Die Notiz steht in der Beschreibung und nicht im
 * Namen - sie ist oft ein halber Satz ("im 2. Gang, Kühlwasser im Auge
 * behalten"), und der gehört nicht in eine Ansage im Vorbeifahren.
 *
 * Grenze und Fähre werden nicht zu einer Tulpe unter vielen: sie bekommen ihr
 * eigenes Symbol, den grossen Warnradius und die eigene Gruppe. Genau dafür
 * gibt es die beiden Formen im Katalog - es sind die Stellen, an denen ein
 * verpasster Hinweis nicht Kilometer kostet, sondern Stunden.
 *
 * Der Name der Fähre ist ihr Hinweis und nicht ihr Kilometerstand: dort steht,
 * wie die Überfahrt heisst und wann sie geht, und genau das soll das Gerät
 * ansagen, wenn die Warnung zwei Kilometer vorher losgeht. „km 84.2 · Fähre“
 * wäre die eine Auskunft, die man in dem Moment nicht braucht.
 */
function tulpenPunkt(tulip: Tulip, at: LatLon, stage: Stage): NavPunkt {
  const grenze = tulip.shape === 'grenze'
  const faehre = tulip.shape === 'faehre'
  const km = tulipKm(tulip.km)
  const was = tulipText(tulip)
  const info = tulipShapeInfo(tulip.shape)

  return {
    art: grenze ? 'grenze' : faehre ? 'faehre' : 'tulpe',
    at,
    name: grenze
      ? `Grenze ${stage.land.trim() || info.label}`
      : faehre
        ? `${info.label} ${tulip.note.trim() || `km ${km}`}`
        : `km ${km} · ${was}`,
    zeilen: [
      { name: 'Bei km', wert: km },
      { name: 'Stelle', wert: was },
      ...zeile('Notiz', tulip.note),
    ],
    fotos: [],
    tagImNamen: false,
  }
}

/**
 * "Tag 03" - der Vorspann, mit dem ein Name seinen Tag nennt.
 *
 * Gemeint ist der Reisetag und nicht die Stelle in der Etappenliste: das Gerät
 * und das gedruckte Buch fahren dieselbe Reise, und wer am Steuer "Tag 03" auf
 * dem Bildschirm liest, schlägt im Buch die Seite mit derselben Zahl auf.
 *
 * Zweistellig, weil die Namen auf dem Gerät alphabetisch sortiert nebeneinander
 * stehen: ohne die Null käme Tag 10 vor Tag 2.
 */
function tagMarke(tag: number): string {
  return `Tag ${dayLabel(tag)}`
}

/** Kilometer und Fahrzeit einer Etappe als ein Satz. */
function fahrtZeile(stage: Stage): string {
  return `${String(Math.round(stage.km))} km · ${String(Math.round(stage.h * 10) / 10)} h`
}

/**
 * Baut aus dem Bestand, was in die Datei soll.
 *
 * Ausgelassene Etappen kommen nicht mit: sie fallen in der App aus allen
 * Summen heraus, und ein Ziel auf dem Gerät, das niemand anfahren wird, ist
 * schlimmer als eine Lücke in der Nummerierung. Gezählt werden sie trotzdem,
 * damit die Vorschau es sagen kann.
 *
 * Eine Lücke in der Nummerierung entsteht dabei gar nicht mehr: nummeriert wird
 * nach Reisetagen, und eine ausgelassene Etappe kostet keinen. Gerechnet wird
 * über trip.stages und nicht über die gewählte Liste - bei einer einzeln
 * ausgewählten Etappe hätte sonst jede den ersten Tag.
 */
export function sammleNavExport(trip: Trip, auswahl: NavAuswahl = {}): NavExport {
  const etappen: NavEtappe[] = []
  let ohneKm = 0
  let ohnePosition = 0
  let ohneStrecke = 0
  let ausgelassen = 0

  const gewaehlt = auswahl.stageId
    ? trip.stages.filter((s) => s.id === auswahl.stageId)
    : trip.stages

  for (const stage of gewaehlt) {
    if (stage.skip) {
      ausgelassen++
      continue
    }

    // Ausgedünnt wird hier und nicht beim Import: die Grenze gehört zur
    // erzeugten Datei und nicht zum Reisedokument. Berechnete Strecken halten
    // sie längst ein (stageRouteFrom in lib/route.ts), eine importierte kann
    // beliebig viele Stützpunkte tragen - und die gingen ungebremst als
    // Garmin-Formpunkte in die GPX-Datei, deren Erzeugung sich ausdrücklich
    // darauf verlässt, dass es höchstens 400 je Etappe sind (siehe
    // formpunkteJeAbschnitt). Beim Import zu kürzen wäre der teurere Weg:
    // eine Datei soll behalten, was in ihr steht, und Ausdünnen ist
    // unwiderruflich - die Reise ginge feiner hinein, als sie zurückkäme.
    // Formtreu ist es ohnehin (Douglas-Peucker), und für eine Zeichnung im
    // Gerät ist nichts verloren.
    const linie = thinGeometry(routeGeometry(stage) ?? [], MAX_GEOMETRY_POINTS)
    if (linie.length < 2) ohneStrecke++

    const tagNr = travelDay(trip.stages, stage)
    const tag = dayLabel(tagNr)

    const gezaehlt = punkteVon(stage, tag, linie)
    const punkte = gezaehlt.punkte
    ohneKm += gezaehlt.ohneKm
    ohnePosition += gezaehlt.ohnePosition

    etappen.push({
      id: stage.id,
      tag: tagNr,
      name: `Tag ${tag} · ${stage.from} → ${stage.to}`,
      zeilen: [
        { name: 'Strecke', wert: fahrtZeile(stage) },
        ...zeile('Land', stage.land),
        ...(stage.optional ? [{ name: 'Puffertag', wert: 'verschiebbar' }] : []),
      ],
      start: stage.c1,
      zwischen: zwischenpunkteVon(stage),
      ziel: {
        art: 'ziel',
        at: stage.c2,
        name: `Tag ${tag} · ${stage.to}`,
        zeilen: [
          { name: 'Fahrt', wert: `${stage.from} → ${stage.to}` },
          { name: 'Strecke', wert: fahrtZeile(stage) },
          ...zeile('Land', stage.land),
          ...stage.night
            .map((text) => ({ name: 'Abends', wert: text.trim() }))
            .filter((z) => z.wert),
        ],
        fotos: fotosVon(auswahl.fotos?.[stage.id]),
        tagImNamen: true,
      },
      punkte,
      linie,
    })
  }

  const einzeln = auswahl.stageId !== undefined && etappen.length === 1
  return {
    titel: einzeln ? `${trip.meta.title} · ${etappen[0]!.name}` : trip.meta.title,
    // Was nicht eingetragen ist, steht auch nicht in der Datei. Ohne das
    // Aussieben beginnt die Beschreibung einer Reise ohne Fahrzeug mit einem
    // Trennpunkt und endet mit einem "Abfahrt" ohne Datum - beides steht dann
    // im Navigationsgerät.
    beschreibung: [
      fahrzeug(trip).name,
      `${String(etappen.length)} ${etappen.length === 1 ? 'Etappe' : 'Etappen'}`,
      trip.meta.start ? `Abfahrt ${trip.meta.start}` : '',
    ]
      .filter(Boolean)
      .join(' · '),
    etappen,
    ohneKm,
    ohnePosition,
    ohneStrecke,
    ausgelassen,
  }
}

// --- Die Ordner der KML -------------------------------------------------------

/** Eine Ortsmarke, so wie sie in der KML steht. */
export interface NavMarke {
  punkt: NavPunkt
  /**
   * Der Name in der Datei.
   *
   * In der Gliederung nach Etappe der Name des Punktes. In der Gliederung nach
   * Art mit vorangestelltem Tag, wo er ihn nicht schon trägt - sonst stünden
   * im Ordner "Camping" sechzehn Plätze ohne Auskunft darüber, an welchem
   * Abend man dort steht.
   */
  name: string
}

/** Eine Linie, so wie sie in der KML steht. */
export interface NavLinie {
  name: string
  punkte: LatLon[]
}

/** Ein Ordner der KML. In Google My Maps ist ein Ordner eine Ebene. */
export interface NavOrdner {
  name: string
  /** Was unter dem Namen steht. */
  zeilen: NavZeile[]
  marken: NavMarke[]
  linien: NavLinie[]
  /** Marken und Linien zusammen - das, was Google je Ebene zählt. */
  objekte: number
}

function ordner(
  name: string,
  zeilen: NavZeile[],
  marken: NavMarke[],
  linien: NavLinie[],
): NavOrdner {
  return { name, zeilen, marken, linien, objekte: marken.length + linien.length }
}

/**
 * Alle Ortsmarken einer Etappe, in der Reihenfolge, in der sie in die KML
 * gehen: erst das Ziel, dann die Zwischenpunkte, dann alles nur Angezeigte.
 *
 * Eine Stelle und nicht zwei, damit die Gliederung nach Etappe und die nach
 * Art dieselben Marken schreiben. Zwei Aufzählungen, die sich einig sein
 * müssten, wären eine zu viel - die Vorschau zählt an dieser einen mit.
 */
function markenVon(etappe: NavEtappe): NavPunkt[] {
  return [etappe.ziel, ...etappe.zwischen, ...etappe.punkte]
}

/** Die Linie einer Etappe, oder nichts, wenn es keine gibt. */
function linieVon(etappe: NavEtappe): NavLinie[] {
  if (etappe.linie.length < 2) return []
  return [{ name: `${etappe.name} · Strecke`, punkte: etappe.linie }]
}

/**
 * Schneidet die Ausgabe in die Ordner der KML.
 *
 * Diese Funktion ist die einzige Stelle, an der die Gliederung entschieden
 * wird - schreibeKml schreibt hin, was hier herauskommt, und navUmfang zählt
 * dasselbe. Zwei Stellen, die sich einig sein müssten, wären eine zu viel: die
 * Vorschau warnt vor Grenzen, die die Datei einhält oder reisst, und eine
 * Vorschau, die etwas anderes zählt als die Datei enthält, ist schlimmer als
 * keine.
 *
 * Leere Ordner fallen weg. In der Gliederung nach Art hat eine Reise ohne
 * Tulpen keinen Ordner "Tulpen" - und vor allem zählt ein Ordner, in dem
 * nichts liegt, sonst gegen die zehn Ebenen von Google My Maps.
 */
export function navOrdner(nav: NavExport, gliederung: NavGliederung): NavOrdner[] {
  if (gliederung === 'etappe') {
    return nav.etappen.map((etappe) =>
      ordner(
        etappe.name,
        etappe.zeilen,
        markenVon(etappe).map((punkt) => ({ punkt, name: punkt.name })),
        linieVon(etappe),
      ),
    )
  }

  const nachArt = new Map<NavArt, NavMarke[]>(NAV_ARTEN.map((art) => [art, []]))
  const linien: NavLinie[] = []

  // Die Reihenfolge innerhalb eines Ordners bleibt die der Reise: im Ordner
  // "Camping" steht der erste Abend oben. Das ist auch die Reihenfolge, in der
  // Google My Maps die Zeilen einer Ebene abschneidet, wenn es zu viele sind -
  // dann fehlt das Ende der Reise und nicht ihre Mitte.
  for (const etappe of nav.etappen) {
    const marke = tagMarke(etappe.tag)
    for (const punkt of markenVon(etappe)) {
      nachArt.get(punkt.art)?.push({
        punkt,
        name: punkt.tagImNamen ? punkt.name : `${marke} · ${punkt.name}`,
      })
    }
    linien.push(...linieVon(etappe))
  }

  const ordnerListe = NAV_ARTEN.map((art) =>
    ordner(
      ARTEN[art].ordner,
      [{ name: '', wert: ARTEN[art].ordnerText }],
      nachArt.get(art) ?? [],
      [],
    ),
  )
  ordnerListe.push(ordner(ORDNER_STRECKEN, [{ name: '', wert: ORDNER_STRECKEN_TEXT }], [], linien))

  return ordnerListe.filter((o) => o.objekte > 0)
}

// --- Umfang und Warnungen -----------------------------------------------------

/**
 * Wie sich eine Gliederung auf die Ebenen auswirkt.
 *
 * Steht für sich, weil die Vorschau beide braucht: die gewählte, um zu warnen,
 * und die andere, um zu sagen, ob ein Umstellen hilft. Ein Rat, der die andere
 * Grenze reisst, ist keiner.
 */
export interface NavEbenen {
  gliederung: NavGliederung
  /** Ordner in der KML. In Google My Maps ist ein Ordner eine Ebene. */
  ebenen: number
  /** Objekte in der vollsten Ebene, und wie sie heisst. */
  groessteEbene: number
  groessteEbeneName: string
}

function navEbenen(nav: NavExport, gliederung: NavGliederung): NavEbenen {
  let groessteEbene = 0
  let groessteEbeneName = ''
  const liste = navOrdner(nav, gliederung)
  for (const o of liste) {
    if (o.objekte > groessteEbene) {
      groessteEbene = o.objekte
      groessteEbeneName = o.name
    }
  }
  return { gliederung, ebenen: liste.length, groessteEbene, groessteEbeneName }
}

/** Was in der Datei landet, in Zahlen. */
export interface NavUmfang extends NavEbenen {
  /** Punkte je Art. */
  proArt: Record<NavArt, number>
  /** Alle Punkte zusammen. */
  punkte: number
  /** Etappen mit gezeichneter Strecke. */
  linien: number
  /** Punkte und Linien zusammen - das, was Google als Objekt zählt. */
  objekte: number
  /** Dieselben Ebenenzahlen für die andere Gliederung. */
  andere: NavEbenen
  fotos: number
  fotoBytes: number
  ohneKm: number
  ohnePosition: number
  ohneStrecke: number
  ausgelassen: number
}

export function navUmfang(
  nav: NavExport,
  gliederung: NavGliederung = NAV_GLIEDERUNG_VORGABE,
): NavUmfang {
  const proArt: Record<NavArt, number> = {
    ziel: 0,
    zwischen: 0,
    camping: 0,
    grenze: 0,
    faehre: 0,
    tulpe: 0,
    hinweis: 0,
  }
  let linien = 0
  let fotos = 0
  let fotoBytes = 0

  for (const etappe of nav.etappen) {
    for (const foto of etappe.ziel.fotos) {
      fotos++
      fotoBytes += foto.bytes
    }
    // Gezählt wird über dieselbe Aufzählung, die auch geschrieben wird:
    // markenVon. Eine eigene Zählung neben ihr könnte ihr widersprechen, und
    // eine Vorschau, die etwas anderes zählt als die Datei enthält, ist
    // schlimmer als keine.
    for (const punkt of markenVon(etappe)) proArt[punkt.art]++
    if (etappe.linie.length >= 2) linien++
  }

  const punkte = NAV_ARTEN.reduce((summe, art) => summe + proArt[art], 0)
  return {
    proArt,
    punkte,
    linien,
    objekte: punkte + linien,
    ...navEbenen(nav, gliederung),
    andere: navEbenen(nav, gliederung === 'art' ? 'etappe' : 'art'),
    fotos,
    fotoBytes,
    ohneKm: nav.ohneKm,
    ohnePosition: nav.ohnePosition,
    ohneStrecke: nav.ohneStrecke,
    ausgelassen: nav.ausgelassen,
  }
}

/**
 * Die Grenzen von Google My Maps.
 *
 * Sie stehen hier, weil sie stillschweigend zuschlagen: der Import meldet
 * Erfolg, die Karte sieht aus wie erwartet, und der Rest fehlt. Genau davor
 * muss die App vorher warnen, mit Zahlen.
 *
 * Belegt in der Hilfe zu My Maps (support.google.com/mymaps/answer/3024836):
 * "Unzipped KML and KMZ files can be up to 5 MB", "Do not import files with
 * more than 2,000 rows. You can import up to 100 photos at a time." Die zehn
 * Ebenen je Karte sind die Grenze der Oberfläche selbst.
 *
 * Google Earth und OsmAnd kennen keine davon.
 */
export const MYMAPS_OBJEKTE_JE_EBENE = 2000
export const MYMAPS_EBENEN = 10
export const MYMAPS_BYTES = 5 * 1024 * 1024
export const MYMAPS_FOTOS = 100

/** Was der Nutzer vor dem Herunterladen wissen muss. */
export interface NavMeldungen {
  /** Etwas geht verloren, wenn er die Datei so benutzt, wie er vorhat. */
  warnungen: string[]
  /** Etwas fehlt in der Datei, das er im Bestand hat. */
  hinweise: string[]
}

function mb(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/**
 * Was das Umstellen der Gliederung brächte - als ein Satz.
 *
 * Eine Warnung, die nur sagt, dass etwas nicht passt, lässt den Nutzer mit der
 * Frage allein, was er tun soll. Und ein Rat, der die andere Grenze reisst,
 * ist schlimmer als keiner: deshalb wird hier nachgerechnet, statt "gliedere
 * nach Art" zu empfehlen und zu hoffen.
 */
function ausweg(andere: NavEbenen): string {
  const name = navGliederungName(andere.gliederung)
  const zuVieleEbenen = andere.ebenen > MYMAPS_EBENEN
  const zuVolleEbene = andere.groessteEbene > MYMAPS_OBJEKTE_JE_EBENE

  if (!zuVieleEbenen && !zuVolleEbene) {
    return (
      `Die Gliederung ${name} legt stattdessen ${String(andere.ebenen)} Ebenen mit höchstens ` +
      `${String(andere.groessteEbene)} Objekten an und passt damit hinein.`
    )
  }
  if (zuVieleEbenen) {
    return (
      `Die Gliederung ${name} legt ${String(andere.ebenen)} Ebenen an - auch das ist mehr als ` +
      `${String(MYMAPS_EBENEN)}. Ein Export je Etappe bleibt unter beiden Grenzen.`
    )
  }
  return (
    `Die Gliederung ${name} kommt auf ${String(andere.ebenen)} Ebenen, dann enthält aber ` +
    `„${andere.groessteEbeneName}“ ${String(andere.groessteEbene)} Objekte. Ein Export je Etappe ` +
    'bleibt unter beiden Grenzen.'
  )
}

/**
 * Prüft den Umfang gegen die Grenzen von Google My Maps.
 *
 * @param kmlBytes Grösse der fertigen KML-Datei. Gemessen und nicht geschätzt:
 *   die Vorschau baut die Datei ohnehin, und eine geschätzte Zahl neben einer
 *   harten Grenze ist keine Auskunft.
 */
export function navMeldungen(umfang: NavUmfang, kmlBytes: number): NavMeldungen {
  const warnungen: string[] = []
  const hinweise: string[] = []

  if (umfang.groessteEbene > MYMAPS_OBJEKTE_JE_EBENE) {
    warnungen.push(
      `Die Ebene „${umfang.groessteEbeneName}“ enthält ${String(umfang.groessteEbene)} Objekte. ` +
        `Google My Maps übernimmt ${String(MYMAPS_OBJEKTE_JE_EBENE)} je Ebene und lässt den Rest ohne Meldung weg. ` +
        ausweg(umfang.andere),
    )
  }
  if (umfang.ebenen > MYMAPS_EBENEN) {
    const woher =
      umfang.gliederung === 'etappe'
        ? `${String(umfang.ebenen)} Ebenen, eine je Etappe`
        : `${String(umfang.ebenen)} Ebenen`
    warnungen.push(
      `Die Datei hat ${woher}. Google My Maps nimmt ${String(MYMAPS_EBENEN)}; ` +
        `die letzten ${String(umfang.ebenen - MYMAPS_EBENEN)} fehlen dort. Google Earth zeigt alle. ` +
        ausweg(umfang.andere),
    )
  }
  if (kmlBytes > MYMAPS_BYTES) {
    warnungen.push(
      `Die KML-Datei ist ${mb(kmlBytes)} gross. Google My Maps nimmt 5 MB. ` +
        'Ein Export je Etappe bleibt darunter.',
    )
  }
  if (umfang.fotoBytes > 0 && umfang.fotoBytes + kmlBytes > MYMAPS_BYTES) {
    warnungen.push(
      `Die KMZ-Datei wird mit den Fotos rund ${mb(umfang.fotoBytes + kmlBytes)} gross. ` +
        'Google My Maps nimmt 5 MB; Google Earth öffnet sie ohne Grenze.',
    )
  }
  if (umfang.fotos > MYMAPS_FOTOS) {
    warnungen.push(
      `Die KMZ-Datei enthält ${String(umfang.fotos)} Fotos. Google My Maps übernimmt ${String(MYMAPS_FOTOS)} auf einmal.`,
    )
  }

  /*
   * Zwei Sätze und nicht einer, denn es sind zwei Ursachen mit zwei Auswegen.
   * Vorher stand für beide derselbe Satz da - "haben einen Kilometerstand, aber
   * keine Position: ihre Etappe hat keine berechnete Strecke" -, und für die
   * Tulpe ohne Kilometerstand auf einer Etappe mit Strecke war daran jede
   * Hälfte falsch. Er schickte damit genau die Leute in den Reiter Etappen, die
   * dort nichts zu tun haben.
   */
  if (umfang.ohneKm > 0) {
    hinweise.push(
      `${String(umfang.ohneKm)} ${umfang.ohneKm === 1 ? 'Tulpe hat' : 'Tulpen haben'} keinen Kilometerstand. ` +
        'Ohne ihn lässt sich keine Stelle auf der Strecke berechnen, deshalb stehen sie in keiner der Dateien. ' +
        'Im Buch stehen sie trotzdem.',
    )
  }
  if (umfang.ohnePosition > 0) {
    hinweise.push(
      `${String(umfang.ohnePosition)} ${umfang.ohnePosition === 1 ? 'Tulpe hat' : 'Tulpen haben'} einen Kilometerstand, ` +
        'aber keine Position: ihre Etappe hat keine berechnete Strecke, oder der Kilometerstand liegt nicht auf ihr. ' +
        'Sie stehen in keiner der Dateien. Wer die Strecke im Reiter Etappen berechnen lässt, bekommt sie mit.',
    )
  }
  if (umfang.ohneStrecke > 0) {
    hinweise.push(
      `${String(umfang.ohneStrecke)} ${umfang.ohneStrecke === 1 ? 'Etappe hat' : 'Etappen haben'} keine berechnete Strecke. ` +
        'Für sie enthält die Datei nur Start und Ziel, keine Linie.',
    )
  }
  if (umfang.ausgelassen > 0) {
    hinweise.push(
      `${String(umfang.ausgelassen)} ausgelassene ${umfang.ausgelassen === 1 ? 'Etappe steht' : 'Etappen stehen'} nicht in der Datei.`,
    )
  }

  return { warnungen, hinweise }
}

// --- XML-Werkzeug -------------------------------------------------------------

/**
 * Zeichen, die in XML 1.0 gar nicht vorkommen dürfen.
 *
 * Steuerzeichen ausser Tabulator, Zeilenumbruch und Wagenrücklauf. Sie stehen
 * in keinem Ortsnamen - aber ein aus einer fremden Datei importierter Bestand
 * kann sie tragen, und ein einziges davon macht die ganze Datei unlesbar. Sie
 * werden entfernt und nicht maskiert: eine Maskierung wäre in XML 1.0
 * genauso verboten wie das Zeichen selbst.
 */
// eslint-disable-next-line no-control-regex
const VERBOTEN = /[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g

/** Text zwischen zwei Elementen. */
export function xmlText(text: string): string {
  return text
    .replace(VERBOTEN, '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

/**
 * Ein Attributwert.
 *
 * Zusätzlich zu den drei Zeichen des Textes werden beide Anführungszeichen
 * maskiert. Geschrieben werden hier zwar nur doppelte, aber ein Wert, der
 * seine eigene Umgebung sprengen kann, sobald jemand die Zeile umstellt, ist
 * ein Fehler auf Vorrat.
 */
export function xmlAttr(text: string): string {
  return xmlText(text).replace(/"/g, '&quot;').replace(/'/g, '&apos;')
}

/**
 * Ein CDATA-Abschnitt für die HTML-Beschreibungen der KML.
 *
 * Das Ende eines solchen Abschnitts lässt sich nicht maskieren - kommt "]]>"
 * im Text vor, muss der Abschnitt dort geschlossen und neu geöffnet werden.
 * Ohne das schlösse eine Notiz mit dieser Zeichenfolge das Element mitten im
 * Satz, und alles danach wäre Markup.
 */
export function cdata(inhalt: string): string {
  return `<![CDATA[${inhalt.replace(VERBOTEN, '').split(']]>').join(']]]]><![CDATA[>')}]]>`
}

/** HTML-Text innerhalb einer CDATA-Beschreibung. */
function htmlText(text: string): string {
  return text
    .replace(VERBOTEN, '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/**
 * Eine Koordinate als Zeichenkette, auf sechs Stellen.
 *
 * Sechs Nachkommastellen sind gut elf Zentimeter - mehr, als eine aus einem
 * Tachostand gerechnete Stelle je sein kann, und weniger Zeichen als die
 * siebzehn Stellen, mit denen JavaScript sonst jede Zahl ausschreibt. Bei
 * vierhundert Stützpunkten je Etappe ist das der Unterschied zwischen einer
 * Datei, die man noch ansehen kann, und einer, die es nicht ist.
 */
function koord(wert: number): string {
  return String(Number(wert.toFixed(6)))
}

/** Zeitpunkt als xsd:dateTime in UTC, wie GPX und KML es verlangen. */
function zeitstempel(zeitpunkt: number): string {
  return new Date(zeitpunkt).toISOString().replace(/\.\d{3}Z$/, 'Z')
}

// --- GPX ----------------------------------------------------------------------

/**
 * Namensräume der GPX-Datei.
 *
 * Der von OsmAnd ist die Adresse aus dessen eigener Dokumentation. Für OsmAnd
 * selbst zählt der Präfix und nicht die Adresse - sein Leser vergleicht die
 * Elementnamen als Text -, und für jeden anderen Leser sagt eine erklärte
 * Adresse, wessen Erweiterung das ist.
 */
const NS_GPX = 'http://www.topografix.com/GPX/1/1'
const NS_OSMAND = 'https://osmand.net/docs/technical/osmand-file-formats/osmand-gpx'
const NS_GPXX = 'http://www.garmin.com/xmlschemas/GpxExtensions/v3'
const NS_TRP = 'http://www.garmin.com/xmlschemas/TripExtensions/v1'

/** Was in creator steht. */
export const ERZEUGER = 'Roadbook'

/** Die Beschreibung eines Punktes als Text, eine Angabe je Zeile. */
function textBeschreibung(zeilen: NavZeile[]): string {
  return zeilen.map((z) => (z.name === '' ? z.wert : `${z.name}: ${z.wert}`)).join('\n')
}

/**
 * Ein Wegpunkt mit allen drei Erweiterungen nebeneinander.
 *
 * Die Reihenfolge der Kindelemente ist nicht frei: das Schema von GPX 1.1
 * schreibt sie vor (ele, time, …, name, cmt, desc, src, link, sym, type, …,
 * extensions). Eine Datei mit desc hinter sym ist kein gültiges GPX, und
 * strenge Leser weisen sie ab.
 */
function wegpunkt(punkt: NavPunkt, einzug: string): string {
  const info = ARTEN[punkt.art]
  const desc = textBeschreibung(punkt.zeilen)
  const z: string[] = []

  z.push(`${einzug}<wpt lat="${koord(punkt.at[0])}" lon="${koord(punkt.at[1])}">`)
  z.push(`${einzug}  <name>${xmlText(punkt.name)}</name>`)
  if (desc !== '') z.push(`${einzug}  <desc>${xmlText(desc)}</desc>`)
  z.push(`${einzug}  <sym>${xmlText(info.sym)}</sym>`)
  // type ist bei OsmAnd die Gruppe, zu der der Punkt gehört.
  z.push(`${einzug}  <type>${xmlText(info.gruppe)}</type>`)
  z.push(`${einzug}  <extensions>`)
  z.push(`${einzug}    <osmand:color>${info.farbe}</osmand:color>`)
  z.push(`${einzug}    <osmand:icon>${info.osmandIcon}</osmand:icon>`)
  z.push(`${einzug}    <osmand:background>${info.osmandForm}</osmand:background>`)
  z.push(`${einzug}    <gpxx:WaypointExtension>`)
  if (info.radiusM > 0) {
    z.push(`${einzug}      <gpxx:Proximity>${String(info.radiusM)}</gpxx:Proximity>`)
  }
  z.push(`${einzug}      <gpxx:DisplayMode>SymbolAndName</gpxx:DisplayMode>`)
  z.push(`${einzug}      <gpxx:Categories>`)
  z.push(`${einzug}        <gpxx:Category>${xmlText(info.gruppe)}</gpxx:Category>`)
  z.push(`${einzug}      </gpxx:Categories>`)
  z.push(`${einzug}    </gpxx:WaypointExtension>`)
  z.push(`${einzug}  </extensions>`)
  z.push(`${einzug}</wpt>`)
  return z.join('\n')
}

interface GpxRoutenpunkt {
  at: LatLon
  name: string
  zeilen: NavZeile[]
  formpunkte: LatLon[]
}

/**
 * Teilt die Linie an den Routenpunkten und hängt jeden Abschnitt an seinen
 * Anfang. Garmin liest gpxx:rpt nicht als angekündigte Zwischenziele, sondern
 * als die Form der Strecke bis zum nächsten rtept.
 *
 * Alle Punkte der Linie bleiben erhalten. Sie sind mit Douglas-Peucker formtreu
 * auf höchstens 400 je Etappe reduziert - beim Berechnen (stageRouteFrom) und
 * für alles, was von woanders kommt, in sammleNavExport; bei der gemessenen
 * 89-km-Strecke ist das ungefähr ein Punkt alle 223 m. Garmins
 * veröffentlichte Grenze von 125 Formpunkten betrifft editierbare Punkte im
 * Routenplaner, nicht diese interne berechnete Geometrie: Das DriveSmart 76
 * schrieb in der Referenzdatei selbst 956 gpxx:rpt in einen einzigen
 * Abschnitt. Ein zweites Ausdünnen würde deshalb nur Kurveninformation
 * vernichten und keinen hier geltenden Grenzwert einhalten.
 */
function formpunkteJeAbschnitt(etappe: NavEtappe): LatLon[][] {
  const anker = [etappe.start, ...etappe.zwischen.map((punkt) => punkt.at), etappe.ziel.at]
  const abschnitte = anker.slice(0, -1).map((): LatLon[] => [])
  if (etappe.linie.length < 2) return abschnitte

  const grenzen = [0]
  let von = 0
  for (const at of anker.slice(1, -1)) {
    let bester = von
    let abstand = Infinity
    for (let i = von; i < etappe.linie.length; i++) {
      const kandidat = haversineM(etappe.linie[i]!, at)
      if (kandidat < abstand) {
        abstand = kandidat
        bester = i
      }
    }
    grenzen.push(bester)
    von = bester
  }
  grenzen.push(etappe.linie.length - 1)

  for (let i = 0; i < abschnitte.length; i++) {
    const anfang = grenzen[i]!
    const ende = grenzen[i + 1]!
    const vonIndex = i === 0 ? anfang : anfang + 1
    abschnitte[i] = etappe.linie
      .slice(vonIndex, ende + 1)
      .filter((at) => haversineM(at, anker[i]!) > 1 && haversineM(at, anker[i + 1]!) > 1)
  }
  return abschnitte
}

/** Die Routenpunkte samt der jeweils von dort beginnenden Geometrie. */
function gpxRoutenpunkte(nav: NavExport): GpxRoutenpunkt[] {
  if (nav.etappen.length === 0) return []

  const erste = nav.etappen[0]!
  const out: GpxRoutenpunkt[] = [{ at: erste.start, name: 'Start', zeilen: [], formpunkte: [] }]
  for (const etappe of nav.etappen) {
    const abschnitte = formpunkteJeAbschnitt(etappe)
    const anfang = out[out.length - 1]!
    anfang.formpunkte = abschnitte[0] ?? []

    for (let i = 0; i < etappe.zwischen.length; i++) {
      const punkt = etappe.zwischen[i]!
      out.push({ ...punkt, formpunkte: abschnitte[i + 1] ?? [] })
    }
    out.push({ ...etappe.ziel, formpunkte: [] })
  }
  return out
}

/** Schreibt ein echtes Ziel mit den Garmin-Erweiterungen für den Folgeabschnitt. */
function gpxRoutenpunkt(punkt: GpxRoutenpunkt): string[] {
  const z: string[] = []
  const desc = textBeschreibung(punkt.zeilen)
  z.push(`    <rtept lat="${koord(punkt.at[0])}" lon="${koord(punkt.at[1])}">`)
  z.push(`      <name>${xmlText(punkt.name)}</name>`)
  if (desc !== '') z.push(`      <desc>${xmlText(desc)}</desc>`)
  z.push('      <extensions>')
  z.push('        <trp:ViaPoint>')
  z.push('          <trp:CalculationMode>FasterTime</trp:CalculationMode>')
  z.push('        </trp:ViaPoint>')
  if (punkt.formpunkte.length > 0) {
    z.push('        <gpxx:RoutePointExtension>')
    for (const at of punkt.formpunkte) {
      z.push(`          <gpxx:rpt lat="${koord(at[0])}" lon="${koord(at[1])}"/>`)
    }
    z.push('        </gpxx:RoutePointExtension>')
  }
  z.push('      </extensions>')
  z.push('    </rtept>')
  return z
}

/** Der Rahmen aller Koordinaten, für metadata/bounds. */
function rahmen(
  nav: NavExport,
): { minLat: number; minLon: number; maxLat: number; maxLon: number } | null {
  let minLat = Infinity
  let minLon = Infinity
  let maxLat = -Infinity
  let maxLon = -Infinity

  const sehen = (at: LatLon) => {
    if (at[0] < minLat) minLat = at[0]
    if (at[0] > maxLat) maxLat = at[0]
    if (at[1] < minLon) minLon = at[1]
    if (at[1] > maxLon) maxLon = at[1]
  }

  for (const etappe of nav.etappen) {
    sehen(etappe.start)
    for (const punkt of markenVon(etappe)) sehen(punkt.at)
    for (const at of etappe.linie) sehen(at)
  }

  return Number.isFinite(minLat) ? { minLat, minLon, maxLat, maxLon } : null
}

/**
 * Schreibt die GPX-Datei.
 *
 * @param zeitpunkt Zeitpunkt des Exports, Millisekunden seit 1970.
 *   Ausdrücklich ein Wert und nicht Date.now(): derselbe Bestand soll
 *   dieselbe Datei ergeben, sonst ist sie nicht prüfbar.
 */
export function schreibeGpx(nav: NavExport, zeitpunkt: number): string {
  const z: string[] = []

  z.push('<?xml version="1.0" encoding="UTF-8"?>')
  z.push(
    `<gpx version="1.1" creator="${xmlAttr(ERZEUGER)}"` +
      `\n  xmlns="${NS_GPX}"` +
      '\n  xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"' +
      `\n  xmlns:osmand="${NS_OSMAND}"` +
      `\n  xmlns:gpxx="${NS_GPXX}"` +
      `\n  xmlns:trp="${NS_TRP}"` +
      `\n  xsi:schemaLocation="${NS_GPX} http://www.topografix.com/GPX/1/1/gpx.xsd` +
      ` ${NS_GPXX} http://www.garmin.com/xmlschemas/GpxExtensionsv3.xsd` +
      ` ${NS_TRP} http://www.garmin.com/xmlschemas/TripExtensionsv1.xsd">`,
  )

  z.push('  <metadata>')
  z.push(`    <name>${xmlText(nav.titel)}</name>`)
  z.push(`    <desc>${xmlText(nav.beschreibung)}</desc>`)
  z.push(`    <time>${zeitstempel(zeitpunkt)}</time>`)
  const r = rahmen(nav)
  if (r) {
    z.push(
      `    <bounds minlat="${koord(r.minLat)}" minlon="${koord(r.minLon)}"` +
        ` maxlat="${koord(r.maxLat)}" maxlon="${koord(r.maxLon)}"/>`,
    )
  }
  z.push('  </metadata>')

  // Erst alle Wegpunkte, dann die Route, dann die Tracks: auch das schreibt
  // das Schema so vor.
  for (const etappe of nav.etappen) {
    for (const punkt of etappe.punkte) z.push(wegpunkt(punkt, '  '))
  }

  if (nav.etappen.length > 0) {
    z.push('  <rte>')
    z.push(`    <name>${xmlText(nav.titel)}</name>`)
    z.push(`    <desc>${xmlText(nav.beschreibung)}</desc>`)
    z.push('    <extensions>')
    z.push('      <trp:Trip>')
    z.push('        <trp:TransportationMode>Automotive</trp:TransportationMode>')
    z.push('      </trp:Trip>')
    z.push('    </extensions>')
    for (const punkt of gpxRoutenpunkte(nav)) z.push(...gpxRoutenpunkt(punkt))
    z.push('  </rte>')
  }

  // Die berechnete Strecke als Track: ein geplanter Korridor, kein
  // aufgezeichneter Weg. Je Etappe einer, damit auf dem Gerät zu sehen ist,
  // welcher Strich zu welchem Tag gehört.
  for (const etappe of nav.etappen) {
    if (etappe.linie.length < 2) continue
    z.push('  <trk>')
    z.push(`    <name>${xmlText(etappe.name)}</name>`)
    z.push(`    <desc>${xmlText(textBeschreibung(etappe.zeilen))}</desc>`)
    z.push('    <trkseg>')
    for (const at of etappe.linie) {
      z.push(`      <trkpt lat="${koord(at[0])}" lon="${koord(at[1])}"/>`)
    }
    z.push('    </trkseg>')
    z.push('  </trk>')
  }

  // Die Gruppeneinstellungen von OsmAnd. Sie stehen am Ende, weil extensions
  // im Schema hinter allem anderen kommt. Das group-Element trägt bewusst
  // keinen Präfix: so steht es in der Dokumentation von OsmAnd, und dessen
  // Leser vergleicht Elementnamen als Text.
  z.push('  <extensions>')
  z.push('    <osmand:points_groups>')
  for (const art of NAV_ARTEN) {
    // Was in der Datei ein Routenpunkt ist, steht in keiner Wegpunktgruppe:
    // eine Gruppe, in der kein Wegpunkt liegt, wäre auf dem Gerät ein leerer
    // Schalter.
    if (ALS_ROUTENPUNKT.includes(art)) continue
    const info = ARTEN[art]
    z.push(
      `      <group name="${xmlAttr(info.gruppe)}" color="${info.farbe}"` +
        ` icon="${info.osmandIcon}" background="${info.osmandForm}"/>`,
    )
  }
  z.push('    </osmand:points_groups>')
  z.push('  </extensions>')
  z.push('</gpx>')

  return `${z.join('\n')}\n`
}

// --- KML und KMZ --------------------------------------------------------------

const NS_KML = 'http://www.opengis.net/kml/2.2'

/**
 * Eine Farbe für KML: aabbggrr statt rrggbb.
 *
 * Die klassische Falle des Formats. Aus #a8322d wird ff2d32a8, nicht
 * ffa8322d - und der Unterschied fällt erst auf, wenn die roten Grenzen auf
 * der Karte blau sind.
 */
export function kmlFarbe(hex: string, deckung = 'ff'): string {
  const rein = hex.replace('#', '')
  const rr = rein.slice(0, 2)
  const gg = rein.slice(2, 4)
  const bb = rein.slice(4, 6)
  return `${deckung}${bb}${gg}${rr}`.toLowerCase()
}

/** Die Beschreibung eines Punktes als HTML-Tabelle, dazu die Fotos. */
function htmlBeschreibung(punkt: NavPunkt): string {
  const teile: string[] = []

  const mitNamen = punkt.zeilen.filter((z) => z.name !== '')
  const ohneNamen = punkt.zeilen.filter((z) => z.name === '')

  if (mitNamen.length > 0) {
    teile.push('<table>')
    for (const z of mitNamen) {
      teile.push(`<tr><th align="left">${htmlText(z.name)}</th><td>${htmlText(z.wert)}</td></tr>`)
    }
    teile.push('</table>')
  }
  for (const z of ohneNamen) teile.push(`<p>${htmlText(z.wert)}</p>`)

  // Die Bilder liegen als Dateien in derselben KMZ und werden relativ
  // angesprochen. In einer blossen KML-Datei zeigt der Verweis ins Leere -
  // deshalb schreibt schreibeKml die Bilder nur, wenn eine KMZ daraus wird.
  for (const foto of punkt.fotos) {
    teile.push(`<img src="${htmlText(foto.pfad)}" width="480"/>`)
  }

  return teile.join('')
}

/** Eine Ortsmarke. */
function ortsmarke(marke: NavMarke, mitFotos: boolean, einzug: string): string {
  const punkt = marke.punkt
  const html = htmlBeschreibung(mitFotos ? punkt : { ...punkt, fotos: [] })
  const z: string[] = []

  z.push(`${einzug}<Placemark>`)
  z.push(`${einzug}  <name>${xmlText(marke.name)}</name>`)
  if (html !== '') z.push(`${einzug}  <description>${cdata(html)}</description>`)
  z.push(`${einzug}  <styleUrl>#rb-${punkt.art}</styleUrl>`)
  z.push(`${einzug}  <Point>`)
  // Länge, Breite - in dieser Reihenfolge, anders als überall sonst in diesem
  // Programm.
  z.push(`${einzug}    <coordinates>${koord(punkt.at[1])},${koord(punkt.at[0])}</coordinates>`)
  z.push(`${einzug}  </Point>`)
  z.push(`${einzug}</Placemark>`)
  return z.join('\n')
}

/**
 * Schreibt die KML-Datei.
 *
 * @param mitFotos ob die Beschreibungen auf Bilddateien verweisen sollen. Nur
 *   für die KMZ: in einer KML ohne Archiv gäbe es die Dateien nicht, und ein
 *   Verweis ins Leere ist in Google Earth ein Kasten mit einem Kreuz darin.
 * @param gliederung wie die Ordner geschnitten werden - siehe NavGliederung.
 *   Am Inhalt ändert das nichts: es steht dasselbe in der Datei, nur anders
 *   einsortiert.
 */
export function schreibeKml(
  nav: NavExport,
  zeitpunkt: number,
  mitFotos = false,
  gliederung: NavGliederung = NAV_GLIEDERUNG_VORGABE,
): string {
  const z: string[] = []

  z.push('<?xml version="1.0" encoding="UTF-8"?>')
  z.push(`<kml xmlns="${NS_KML}">`)
  z.push('  <Document>')
  z.push(`    <name>${xmlText(nav.titel)}</name>`)
  z.push(
    `    <description>${cdata(`<p>${htmlText(nav.beschreibung)}</p><p>Erzeugt von ${ERZEUGER} am ${zeitstempel(zeitpunkt)}</p>`)}</description>`,
  )

  for (const art of NAV_ARTEN) {
    const info = ARTEN[art]
    z.push(`    <Style id="rb-${art}">`)
    z.push('      <IconStyle>')
    z.push(`        <color>${kmlFarbe(info.farbe)}</color>`)
    z.push('        <scale>1.1</scale>')
    z.push(`        <Icon><href>${xmlText(info.kmlIcon)}</href></Icon>`)
    z.push('      </IconStyle>')
    z.push('      <LabelStyle><scale>0.9</scale></LabelStyle>')
    z.push('    </Style>')
  }
  z.push('    <Style id="rb-linie">')
  z.push('      <LineStyle>')
  z.push(`        <color>${kmlFarbe(ARTEN.ziel.farbe, 'cc')}</color>`)
  z.push('        <width>4</width>')
  z.push('      </LineStyle>')
  z.push('    </Style>')

  for (const o of navOrdner(nav, gliederung)) {
    z.push('    <Folder>')
    z.push(`      <name>${xmlText(o.name)}</name>`)
    const kopf = o.zeilen
      .map((zl) =>
        zl.name === '' ? htmlText(zl.wert) : `${htmlText(zl.name)}: ${htmlText(zl.wert)}`,
      )
      .join('<br/>')
    if (kopf !== '') z.push(`      <description>${cdata(kopf)}</description>`)

    for (const marke of o.marken) z.push(ortsmarke(marke, mitFotos, '      '))

    for (const linie of o.linien) {
      z.push('      <Placemark>')
      z.push(`        <name>${xmlText(linie.name)}</name>`)
      z.push('        <styleUrl>#rb-linie</styleUrl>')
      z.push('        <LineString>')
      // tessellate legt die Linie auf den Boden; ohne das schwebt sie in
      // Google Earth über Tälern und hängt in Hügeln.
      z.push('          <tessellate>1</tessellate>')
      z.push(
        `          <coordinates>${linie.punkte.map((at) => `${koord(at[1])},${koord(at[0])}`).join(' ')}</coordinates>`,
      )
      z.push('        </LineString>')
      z.push('      </Placemark>')
    }

    z.push('    </Folder>')
  }

  z.push('  </Document>')
  z.push('</kml>')

  return `${z.join('\n')}\n`
}

/** Ein Foto mit seinen Bytes, so wie es in die KMZ geht. */
export interface KmzFoto {
  pfad: string
  bytes: Uint8Array
}

/**
 * Packt KML und Fotos in eine KMZ.
 *
 * Die doc.kml steht zuerst im Archiv: die Norm für KMZ sagt, dass die erste
 * KML-Datei im Archiv die des Dokuments ist, und Leser, die nicht das ganze
 * Verzeichnis durchgehen, finden sie so ohne Suche.
 *
 * Fotos, auf die keine Beschreibung verweist, werden nicht mitgepackt - ein
 * Bild im Archiv, das niemand anzeigt, ist nur Gewicht.
 */
export function packeKmz(
  kml: string,
  fotos: KmzFoto[],
  zeitpunkt: number,
): Uint8Array<ArrayBuffer> {
  const eintraege: ZipEintrag[] = [{ name: 'doc.kml', bytes: new TextEncoder().encode(kml) }]
  for (const foto of fotos) {
    eintraege.push({ name: foto.pfad, bytes: foto.bytes })
  }
  return zipSchreiben(eintraege, zeitpunkt)
}

// --- Dateinamen ---------------------------------------------------------------

/**
 * Ein Dateiname aus dem Titel der Ausgabe.
 *
 * Nur Kleinbuchstaben, Ziffern und Bindestriche: der Name landet auf einem
 * Speicher, den ein Navigationsgerät liest, und deren Dateisysteme sind mit
 * Umlauten, Pfeilen und Doppelpunkten schon oft genug gescheitert. Umlaute
 * werden dabei ausgeschrieben und nicht weggeworfen, sonst würde aus "Bühl"
 * ein "bhl".
 */
export function navDateiname(titel: string, endung: string, datum: string): string {
  const kern = titel
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60)
    .replace(/-+$/g, '')

  return `${kern === '' ? 'roadbook' : kern}-${datum}.${endung}`
}
